import { useEffect, useState } from 'react';

import clsx from 'clsx';
import dynamic from 'next/dynamic';

import {
  GridContainerBlockFragment,
  isGridFourcolBlock,
  isGridThreecolBlock,
  isGridTwocolBlock,
  isImageBlock,
  isMediaWithCaptionBlock,
  isTextBlock,
} from '@hultafors/hultaforsgroup/types';

import { ImageBlock } from '../image-block/image-block';
import { TextBlock } from '../text-block/text-block';

import styles from './grid-container-block.module.scss';

const GridItem = dynamic(() =>
  import('@hultafors/shared/components').then((mod) => mod.GridItem),
);
const GridContainer = dynamic(() =>
  import('@hultafors/shared/components').then((mod) => mod.GridContainer),
);
const GridRow = dynamic(() =>
  import('@hultafors/shared/components').then((mod) => mod.GridRow),
);

interface DesktopColumnsData {
  columnSize: number;
}

export const GridContainerBlock: React.FC<GridContainerBlockFragment> = ({
  content = [],
  fullwidth,
  gap,
  backgroundColor,
  textColor,
}) => {
  const [columnsData, setColumnsData] = useState<DesktopColumnsData>({
    columnSize: 6,
  });

  const style = {
    ['--background-color' as string]: backgroundColor?.hex,
    ['--text-color' as string]: textColor?.hex,
    ['--button-text-color-hover' as string]: textColor?.hex,
  };

  const getColumnsData = () => {
    content.forEach((gridRow) => {
      if (isGridThreecolBlock(gridRow)) {
        setColumnsData({
          columnSize: 4,
        });
        return;
      }

      if (isGridFourcolBlock(gridRow)) {
        setColumnsData({
          columnSize: 3,
        });
        return;
      }

      return columnsData;
    });

    return columnsData;
  };

  const contentMapper = (block: any, index: number) => {
    if (isImageBlock(block)) {
      return (
        <ImageBlock
          key={`image-block-${index}`}
          image={block.image}
          imageLoad={block.imageLoad}
          fullWidth={true}
        />
      );
    }
    if (isTextBlock(block)) {
      return (
        <TextBlock {...block} fullwidth={true} key={`text-block-${index}`} />
      );
    }
    if (isMediaWithCaptionBlock(block)) {
      return (
        <div
          className={clsx(
            styles['media-with-caption'],
            block.centerText && styles['media-with-caption__center'],
            block.textOnImage && styles['media-with-caption__text-on-image'],
          )}
          style={{
            aspectRatio: block.textOnImage
              ? block.media?.image?.responsiveImage?.aspectRatio || 16 / 9
              : undefined,
          }}
          key={`media-with-caption-block-${index}`}
        >
          {block.media && (
            <ImageBlock
              image={block.media?.image}
              imageLoad={block.media?.imageLoad}
              fullWidth={true}
              isBackground={block.textOnImage}
            />
          )}
          {block.caption && <TextBlock {...block.caption} fullwidth={true} />}
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    if (!content.length) {
      return;
    }

    getColumnsData();
  }, [content]);

  if (!content.length) {
    return null;
  }

  return (
    <GridContainer
      gap={gap}
      fullwidth={fullwidth}
      className={clsx(
        styles['grid-container-block'],
        gap && styles['grid-container-block__gap'],
        fullwidth && styles['grid-container-block__full-width'],
      )}
      style={style}
    >
      {content.map((gridRows) => {
        return (
          <GridRow
            key={`Grid-row-${gridRows.id}`}
            gap={gap}
            className={clsx(
              styles['grid-row'],
              !fullwidth && 'content-container wide',
            )}
          >
            {gridRows.content.map((block, i) => {
              return isGridTwocolBlock(block) ? (
                <GridItem
                  key={`Grid-content-${block.id}`}
                  columnSize={columnsData?.columnSize}
                  className={styles['grid-item-row']}
                >
                  {block.content.map(contentMapper)}
                </GridItem>
              ) : (
                <GridItem
                  key={`Grid-content-${block.id}`}
                  columnSize={columnsData?.columnSize}
                  className={styles['grid-item']}
                >
                  {contentMapper(block, i)}
                </GridItem>
              );
            })}
          </GridRow>
        );
      })}
    </GridContainer>
  );
};
